// Schedule settings (Lane 1344, UI redesign slice 3, plan section 11): the number of
// rounds the buyer plans, a date per round not yet open, and an award date. A plan
// only: suppliers never see it. The one exception is the open round's due time,
// which the server moves with the existing extend logic (later only, suppliers told).
// Plans are calendar days, shown and compared in the buyer's time zone; a real
// round is a time. Dates can be out of order (a due time can move past a plan):
// the screen marks them "Needs updating" and never assumes the order.
import React, { useState } from 'react';
import { formatDate } from './sourcingApi';
import { formatDueAt } from '../sourcing-portal/portalView';
import { localDay, readLocal, roundErrorText, toLocalInput } from './RoundTab';

const MIN_LEAD_MS = 5 * 60 * 1000;
export const ROUNDS_OFFERED = 3;

function viewerZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; }
}

// The rows the Overview shows after the opened rounds: one per planned round (its
// date, "No date", "Date passed" or "Needs updating"), then Award by.
export function planRows(overview, now = new Date()) {
  const s = overview.schedule || {};
  const current = overview.current_round || 0;
  const round = (overview.rounds || []).find((r) => r.round === current);
  const today = localDay(now);
  let prev = round ? localDay(round.closed_at || round.due_at) : '';
  const byRound = Object.fromEntries((s.planned || []).map((p) => [p.round, String(p.due_date).slice(0, 10)]));
  const rows = [];
  const top = Math.max(s.planned_rounds || 0, ...Object.keys(byRound).map(Number), 0);
  for (let n = Math.max(current + 1, 1); n <= top; n += 1) {
    const day = byRound[n];
    let state = 'none';
    if (day) {
      if (prev && day <= prev) state = 'conflict';
      else if (day < today) state = 'passed';
      else state = 'planned';
      // The latest day so far: a date out of order never hides a later conflict
      // (Codex s3 r2).
      if (!prev || day > prev) prev = day;
    }
    rows.push({ round: n, day, state });
  }
  const award = s.award_by ? String(s.award_by).slice(0, 10) : '';
  return { rows, award, awardConflict: Boolean(award && prev && award < prev) };
}

export const PLAN_STATE_TEXT = { planned: 'Planned', passed: 'Date passed', conflict: 'Needs updating', none: 'No date' };

// The problems the server would refuse, said before Save (the server checks again).
function problemOf({ count, current, round, openRound, dueLocal, initialDue, dates, award }) {
  if (openRound && dueLocal !== initialDue) {
    const due = readLocal(dueLocal);
    if (!due) return 'Enter the due time of the open round.';
    if (due.getTime() <= new Date(openRound.due_at).getTime()) return 'The due time can only move later. To end the round early, close it.';
    if (due.getTime() <= Date.now() + MIN_LEAD_MS) return 'The due time must be at least 5 minutes from now.';
  }
  // The day the current round ends: the (new) due time of an open round, the
  // close or due time of a closed one (Codex s3 r1 #3).
  let prev = '';
  if (openRound) prev = localDay(readLocal(dueLocal) || openRound.due_at);
  else if (round) prev = localDay(round.closed_at || round.due_at);
  let prevName = round ? 'the current round' : '';
  let last = 0;
  for (let n = Math.max(current + 1, 1); n <= count; n += 1) {
    const day = dates[n];
    if (!day) continue;
    if (prev && day <= prev) return `Round ${n}'s date must be after ${prevName} (${formatDate(prev)}).`;
    prev = day;
    prevName = `round ${n}`;
    last = n;
  }
  if (award && prev && award < prev) return `Award by must be on or after ${last ? `round ${last}'s date` : "the current round's due date"}.`;
  return '';
}

export default function ScheduleSettings({ api, eventId, overview, onClose, onSaved }) {
  const s = overview.schedule || {};
  const current = overview.current_round || 0;
  const draft = overview.status === 'draft';
  const round = (overview.rounds || []).find((r) => r.round === current);
  const openRound = !draft && round && !round.closed_at && overview.status === 'open' ? round : null;
  const lowest = Math.max(current, 1);
  const highest = Math.max(ROUNDS_OFFERED, current);
  const [count, setCount] = useState(() => Math.min(highest, Math.max(lowest, s.planned_rounds || lowest)));
  const initialDue = openRound ? toLocalInput(openRound.due_at) : '';
  const [dueLocal, setDueLocal] = useState(initialDue);
  const [dates, setDates] = useState(() => Object.fromEntries((s.planned || [])
    .map((p) => [p.round, String(p.due_date).slice(0, 10)])));
  const [award, setAward] = useState(s.award_by ? String(s.award_by).slice(0, 10) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const problem = problemOf({ count, current, round, openRound, dueLocal, initialDue, dates, award });
  const counts = [];
  for (let n = lowest; n <= highest; n += 1) counts.push(n);

  const save = async () => {
    setBusy(true);
    setError('');
    const planned = [];
    for (let n = Math.max(current + 1, 1); n <= count; n += 1) {
      if (dates[n]) planned.push({ round: n, due_date: dates[n] });
    }
    const input = { planned_rounds: count, planned, award_by: award || null, time_zone: viewerZone() };
    if (openRound && dueLocal !== initialDue) input.current_due_at = readLocal(dueLocal).toISOString();
    try {
      await api.roundAction(eventId, 'set_schedule', input, overview.revision);
      onSaved({ stale: false });
    } catch (e) {
      // A schedule changed by someone else: reload it before another save
      // (Codex plan round 1).
      if (e.code === 'stale_revision') onSaved({ stale: true }); else setError(roundErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="srce-overlay" role="dialog" aria-label="Schedule settings">
      <div className="srce-modal srce-modal--narrow">
        <div className="srce-card2-h"><span className="srce-card2-t">Schedule settings</span>
          <button type="button" className="srce-chip-x srce-push" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" /></svg>
          </button></div>
        <div className="srce-modal-b">
          {error && <div className="srce-error" role="alert">{error}</div>}
          <div className="srce-field">
            <label className="srce-label" htmlFor="srce-sched-rounds">Rounds</label>
            <select id="srce-sched-rounds" className="srce-input srce-input--short" value={count}
              onChange={(e) => setCount(Number(e.target.value))}>
              {counts.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {current > 1 && <span className="srce-mut srce-hint">This event is in round {current}; the count cannot be lower.</span>}
          </div>
          {(overview.rounds || []).filter((r) => r.round < current || (r.round === current && !openRound)).map((r) => (
            <div key={r.round} className="srce-field">
              <span className="srce-label">Round {r.round}</span>
              <span className="srce-mut">{r.closed_at ? `Closed ${formatDueAt(r.closed_at)}` : `Due ${formatDueAt(r.due_at)}`}</span>
            </div>
          ))}
          {openRound && (
            <div className="srce-field">
              <label className="srce-label" htmlFor="srce-sched-due">Round {current} due (open)</label>
              <input id="srce-sched-due" className="srce-input" type="datetime-local" value={dueLocal}
                min={toLocalInput(new Date(Math.max(new Date(openRound.due_at).getTime(), Date.now() + MIN_LEAD_MS)))}
                onChange={(e) => setDueLocal(e.target.value)} />
              <span className="srce-mut srce-hint">Suppliers see a new due date as soon as you save. You can only move it later.</span>
            </div>
          )}
          {Array.from({ length: Math.max(0, count - current) }, (_, i) => current + 1 + i).map((n) => (
            <div key={n} className="srce-field">
              <label className="srce-label" htmlFor={`srce-sched-r${n}`}>Round {n} due{draft && n === 1 ? ' (when you publish)' : ''}</label>
              <input id={`srce-sched-r${n}`} className="srce-input srce-input--short" type="date" value={dates[n] || ''}
                onChange={(e) => setDates((d) => ({ ...d, [n]: e.target.value }))} />
              {!draft || n > 1 ? <span className="srce-mut srce-hint">A plan. It fills in the due time when you open the round.</span>
                : <span className="srce-mut srce-hint">The "Offers due" date. It fills in the due time when you publish.</span>}
            </div>
          ))}
          <div className="srce-field">
            <label className="srce-label" htmlFor="srce-sched-award">Award by</label>
            <input id="srce-sched-award" className="srce-input srce-input--short" type="date" value={award}
              onChange={(e) => setAward(e.target.value)} />
            <span className="srce-mut srce-hint">A plan only. Suppliers see these dates only on a negotiation deck you build.</span>
          </div>
          {problem && <div className="srce-sched-problem" role="status">{problem}</div>}
        </div>
        <div className="srce-modal-f">
          <span className="srce-push srce-row">
            <button type="button" className="srce-btn" onClick={onClose}>Cancel</button>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || Boolean(problem)} onClick={save}>
              {busy ? 'Saving…' : 'Save'}</button>
          </span>
        </div>
      </div>
    </div>
  );
}
